import {MessageDescriptor} from '@lingui/core';
import {t} from '@lingui/macro';

import {csatEditorBaseErrorLabels, CsatEditorBaseErrorsEnum} from '../csatEditor/csatEditorErrors';
import {
  expressionsEditorBaseErrorLabels,
  ExpressionsEditorBaseErrorsEnum,
} from '../expressions/expressionsEditorErrors';
import {
  requiredTaggingEditorBaseErrorLabels,
  RequiredTaggingEditorBaseErrorsEnum,
} from '../requiredTaggingEditor/requiredTaggingEditorErrors';
import {ruleDataTableErrorLabels, RuleDataTableErrors} from '../ruleEditor/dataTableErrors';
import {
  ruleEditorActionBaseErrorLabels,
  RuleEditorActionBaseErrorsEnum,
  ruleEditorConditionBaseErrorLabels,
  RuleEditorConditionBaseErrorsEnum,
  ruleEditorTriggerBaseErrorLabels,
  RuleEditorTriggerBaseErrorsEnum,
} from '../ruleEditor/ruleEditorErrors';
import {slaEditorBaseErrorLabels, SlaEditorBaseErrorsEnum} from '../slaEditor/slaEditorErrors';

/*
 * Types.
 */

export enum EditorBaseErrorsEnum {
  MISSING_NAME = 'MISSING_NAME',
}

export enum EditorValidationErrorsEnum {
  EMPTY_ARRAY = 'EMPTY_ARRAY',
  EMPTY_STRING = 'EMPTY_STRING',
  MISSING_BODY = 'MISSING_BODY',
  MISSING_CHANNEL = 'MISSING_CHANNEL',
  MISSING_CHANNELS = 'MISSING_CHANNELS',
  MISSING_COMPARATOR = 'MISSING_COMPARATOR',
  MISSING_CUSTOM_FIELD = 'MISSING_CUSTOM_FIELD',
  MISSING_DAYS = 'MISSING_DAYS',
  MISSING_DIRECTION = 'MISSING_DIRECTION',
  MISSING_EMAIL_RECIPIENT = 'MISSING_EMAIL_RECIPIENT',
  MISSING_EXPRESSION = 'MISSING_EXPRESSION',
  MISSING_FIELD = 'MISSING_FIELD',
  MISSING_GROUP = 'MISSING_GROUP',
  MISSING_INBOX = 'MISSING_INBOX',
  MISSING_INBOXES = 'MISSING_INBOXES',
  MISSING_LANGUAGE = 'MISSING_LANGUAGE',
  MISSING_LINK_NAME = 'MISSING_LINK_NAME',
  MISSING_REQUIRED_TAGGING_ID = 'MISSING_REQUIRED_TAGGING_ID',
  MISSING_RESPONSE = 'MISSING_RESPONSE',
  MISSING_TAG = 'MISSING_TAG',
  MISSING_TAGS = 'MISSING_TAGS',
  MISSING_TEAMMATE = 'MISSING_TEAMMATE',
  MISSING_TEAMMATES = 'MISSING_TEAMMATES',
  MISSING_TEXT = 'MISSING_TEXT',
  MISSING_TIMEZONE = 'MISSING_TIMEZONE',
  MISSING_TIME_RANGE = 'MISSING_TIME_RANGE',
  MISSING_TYPE = 'MISSING_TYPE',
  MISSING_SNOOZE_PRESET = 'MISSING SNOOZE PRESET',
  MISSING_VALUE = 'MISSING_VALUE',
  MISSING_EXTENSIONS = 'MISSING_EXTENSIONS',
  UNKNOWN_ERROR = 'UNKNOWN_ERROR',
  UNDEFINED_PARAMETER = 'UNDEFINED_PARAMETER',

  // TODO Legacy, will be deleted in next wave
  VALIDATION_ERRORS = 'VALIDATION_ERRORS',
}

// TODO : delete when common elements such as footer are refactored; there is no global editor, only specific ones
export const EditorErrorsEnum = {
  ...CsatEditorBaseErrorsEnum,
  ...EditorBaseErrorsEnum,
  ...RuleEditorActionBaseErrorsEnum,
  ...RuleEditorConditionBaseErrorsEnum,
  ...RuleEditorTriggerBaseErrorsEnum,
  ...ExpressionsEditorBaseErrorsEnum,
  ...RequiredTaggingEditorBaseErrorsEnum,
  ...EditorValidationErrorsEnum,
  ...SlaEditorBaseErrorsEnum,
  ...RuleDataTableErrors,
};

export type EditorErrorsEnum =
  | CsatEditorBaseErrorsEnum
  | EditorBaseErrorsEnum
  | RuleEditorActionBaseErrorsEnum
  | RuleEditorConditionBaseErrorsEnum
  | RuleEditorTriggerBaseErrorsEnum
  | ExpressionsEditorBaseErrorsEnum
  | RequiredTaggingEditorBaseErrorsEnum
  | EditorValidationErrorsEnum
  | SlaEditorBaseErrorsEnum
  | RuleDataTableErrors;

/*
 * Constants.
 */

export const editorValidationErrorLabels: Record<EditorValidationErrorsEnum, MessageDescriptor> = {
  [EditorValidationErrorsEnum.MISSING_BODY]: t('rule-editor-card-error-missing-body')`Missing body`,
  [EditorValidationErrorsEnum.MISSING_CHANNEL]: t('rule-editor-card-error-missing-channel')`Missing channel`,
  [EditorValidationErrorsEnum.MISSING_CHANNELS]: t(
    'rule-editor-card-error-missing-channels',
  )`Missing channels`,
  [EditorValidationErrorsEnum.MISSING_COMPARATOR]: t(
    'rule-editor-card-error-missing-comparator',
  )`Missing comparator`,
  [EditorValidationErrorsEnum.MISSING_CUSTOM_FIELD]: t(
    'rule-editor-card-error-missing-custom-field',
  )`Missing custom field`,
  [EditorValidationErrorsEnum.MISSING_DAYS]: t('rule-editor-card-error-missing-days')`Missing days`,
  [EditorValidationErrorsEnum.MISSING_DIRECTION]: t(
    'rule-editor-card-error-missing-direction',
  )`Missing message direction`,
  [EditorValidationErrorsEnum.MISSING_EMAIL_RECIPIENT]: t(
    'rule-editor-card-error-missing-email-recipient',
  )`Missing email recipient`,
  [EditorValidationErrorsEnum.MISSING_EXPRESSION]: t(
    'rule-editor-card-error-missing-expression',
  )`Missing expression`,
  [EditorValidationErrorsEnum.MISSING_FIELD]: t('rule-editor-card-error-missing-field')`Missing field`,
  [EditorValidationErrorsEnum.MISSING_GROUP]: t('rule-editor-card-error-missing-group')`Missing group`,
  [EditorValidationErrorsEnum.MISSING_INBOX]: t('rule-editor-card-error-missing-inbox')`Missing inbox`,
  [EditorValidationErrorsEnum.MISSING_INBOXES]: t('rule-editor-card-error-missing-inboxes')`Missing inboxes`,
  [EditorValidationErrorsEnum.MISSING_LANGUAGE]: t(
    'rule-editor-card-error-missing-language',
  )`Missing language`,
  [EditorValidationErrorsEnum.MISSING_LINK_NAME]: t(
    'rule-editor-card-error-missing-link-name',
  )`Missing link name`,
  [EditorValidationErrorsEnum.MISSING_REQUIRED_TAGGING_ID]: t(
    'rule-editor-card-error-missing-required-tagging',
  )`Missing required tagging id`,
  [EditorValidationErrorsEnum.MISSING_RESPONSE]: t(
    'rule-editor-card-error-missing-response',
  )`Missing response`,
  [EditorValidationErrorsEnum.MISSING_TAG]: t('rule-editor-card-error-missing-tag')`Missing tag`,
  [EditorValidationErrorsEnum.MISSING_TAGS]: t('rule-editor-card-error-missing-tags')`Missing tags`,
  [EditorValidationErrorsEnum.MISSING_TEAMMATE]: t(
    'rule-editor-card-error-missing-teammate',
  )`Missing teammate`,
  [EditorValidationErrorsEnum.MISSING_TEAMMATES]: t(
    'rule-editor-card-error-missing-teammates',
  )`Missing teammates`,
  [EditorValidationErrorsEnum.MISSING_TEXT]: t('rule-editor-card-error-missing-text')`Missing text`,
  [EditorValidationErrorsEnum.MISSING_TIMEZONE]: t(
    'rule-editor-card-error-missing-timezone',
  )`Missing timezone`,
  [EditorValidationErrorsEnum.MISSING_TIME_RANGE]: t(
    'rule-editor-card-error-missing-time-range',
  )`Missing time range`,
  [EditorValidationErrorsEnum.MISSING_TYPE]: t('rule-editor-card-error-missing-type')`Missing type`,
  [EditorValidationErrorsEnum.MISSING_SNOOZE_PRESET]: t(
    'rule-editor-card-error-missing-snooze-preset',
  )`Missing snooze preset`,
  [EditorValidationErrorsEnum.MISSING_VALUE]: t('rule-editor-card-error-missing-value')`Missing value`,
  [EditorValidationErrorsEnum.MISSING_EXTENSIONS]: t(
    'rule-editor-card-error-missing-extensions',
  )`Missing extensions`,
  [EditorValidationErrorsEnum.EMPTY_ARRAY]: t(
    'rule-editor-card-error-empty-array',
  )`Empty arrays are not allowed`,
  [EditorValidationErrorsEnum.EMPTY_STRING]: t(
    'rule-editor-card-error-empty string',
  )`Empty strings are not allowed`,
  [EditorValidationErrorsEnum.UNDEFINED_PARAMETER]: t(
    'rule-editor-card-error-undefined-parameter',
  )`Parameter is not defined`,
  [EditorValidationErrorsEnum.UNKNOWN_ERROR]: t('rule-editor-card-error-unknown')`Unknown error`,

  // TODO: legacy, delete
  [EditorValidationErrorsEnum.VALIDATION_ERRORS]: t(
    'rule-editor-footer-tooltip-warning-validation-errors',
  )`Some of your rules are invalid`,
};

export const editorBaseErrorLabels: Record<EditorBaseErrorsEnum, MessageDescriptor> = {
  [EditorBaseErrorsEnum.MISSING_NAME]: t('rule-editor-footer-tooltip-warning-missing-name')`Name is required`,
};

const editorErrorLabels: Record<EditorErrorsEnum, MessageDescriptor> = {
  ...csatEditorBaseErrorLabels,
  ...editorBaseErrorLabels,
  ...ruleEditorActionBaseErrorLabels,
  ...ruleEditorConditionBaseErrorLabels,
  ...ruleEditorTriggerBaseErrorLabels,
  ...expressionsEditorBaseErrorLabels,
  ...requiredTaggingEditorBaseErrorLabels,
  ...editorValidationErrorLabels,
  ...slaEditorBaseErrorLabels,
  ...ruleDataTableErrorLabels,
};

/*
 * Helpers.
 */

// TODO: delete when editors errors are decoupled
export function getEditorErrorLabel(error: EditorErrorsEnum) {
  return editorErrorLabels[error];
}

export function getEditorValidationErrorFromPath(path: string) {
  // In some cases, the error is nested (i.e. `range.start_time` in the 'Time is between' card).
  // The root gives enough guidance to the user.
  const root = path.split('.')[0];

  switch (root) {
    case 'body':
      return EditorValidationErrorsEnum.MISSING_BODY;

    case 'channel':
      return EditorValidationErrorsEnum.MISSING_CHANNEL;

    case 'channels':
      return EditorValidationErrorsEnum.MISSING_CHANNELS;

    case 'comparator':
      return EditorValidationErrorsEnum.MISSING_COMPARATOR;

    case 'custom_field':
      return EditorValidationErrorsEnum.MISSING_CUSTOM_FIELD;

    case 'days':
      return EditorValidationErrorsEnum.MISSING_DAYS;

    case 'expression':
      return EditorValidationErrorsEnum.MISSING_EXPRESSION;

    case 'field': // ??
      return EditorValidationErrorsEnum.MISSING_FIELD;

    case 'group':
      return EditorValidationErrorsEnum.MISSING_GROUP;

    case 'inbox':
      return EditorValidationErrorsEnum.MISSING_INBOX;

    case 'inboxes':
      return EditorValidationErrorsEnum.MISSING_INBOXES;

    case 'language':
      return EditorValidationErrorsEnum.MISSING_LANGUAGE;

    case 'name':
      return EditorValidationErrorsEnum.MISSING_LINK_NAME;

    case 'range':
      return EditorValidationErrorsEnum.MISSING_TIME_RANGE;

    case 'recipientEmail':
      return EditorValidationErrorsEnum.MISSING_EMAIL_RECIPIENT;

    case 'response':
      return EditorValidationErrorsEnum.MISSING_RESPONSE;

    case 'safety_net_id':
      return EditorValidationErrorsEnum.MISSING_REQUIRED_TAGGING_ID;

    case 'snoozePreset':
      return EditorValidationErrorsEnum.MISSING_SNOOZE_PRESET;

    case 'tag':
      return EditorValidationErrorsEnum.MISSING_TAG;

    case 'tags':
      return EditorValidationErrorsEnum.MISSING_TAGS;

    case 'teammate':
      return EditorValidationErrorsEnum.MISSING_TEAMMATE;

    case 'teammates':
      return EditorValidationErrorsEnum.MISSING_TEAMMATES;

    case 'text':
      return EditorValidationErrorsEnum.MISSING_TEXT;

    case 'timezone':
      return EditorValidationErrorsEnum.MISSING_TIMEZONE;

    case 'type': // message type
      return EditorValidationErrorsEnum.MISSING_TYPE;

    case 'value':
      return EditorValidationErrorsEnum.MISSING_VALUE;

    case 'extensions':
      return EditorValidationErrorsEnum.MISSING_EXTENSIONS;

    default:
      return EditorValidationErrorsEnum.UNKNOWN_ERROR;
  }
}
